// @flow
import type {Node} from 'react'
import React, {useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'
import Select from 'react-select'

import sortByProp from 'utils/MiscUtils/sortByProp'
import type {Option} from 'components/proposals/types'
import liftNodes from 'utils/GraphQL/typedLiftNodes'
import type {
  SelectRole_tension$key as Tension,
} from './__generated__/SelectRole_tension.graphql'

const tensionFragment = graphql`
  fragment SelectRole_tension on Tension {
    availableRoles {
      edges {
        node {
          id
          nameWithCircleForCoreRoles: localizedName(with: CIRCLE_FOR_CORE_ROLES)
        }
      }
    }

    circle {
      supportedRole {
        id
        databaseId
        isExpanded
        nameWithCircleForCoreRoles: localizedName(with: CIRCLE_FOR_CORE_ROLES)
      }

      roles {
        edges {
          node {
            id
            nameWithCircleForCoreRoles: localizedName(with: CIRCLE_FOR_CORE_ROLES)
          }
        }
      }
    }
  }
`

type Props = {
  isReadonly: boolean,
  isMeeting: boolean,
  onChange: Option => void,
  tension: Tension,
  value: ?string,
}

const makeOption = ({id: value, nameWithCircleForCoreRoles: label}): Option => ({label, value})

function SelectRole({isReadonly, isMeeting, onChange, tension: tensionKey, value}: Props): Node {
  const tension = useFragment(tensionFragment, tensionKey)

  const roles = useMemo(
    () => {
      if (tension.circle?.supportedRole && !tension.circle.supportedRole.isExpanded)
        return [tension.circle.supportedRole]

      if (isMeeting)
        return liftNodes(tension.circle?.roles)

      return liftNodes(tension.availableRoles)
    },
    [isMeeting, tension],
  )

  const options = useMemo(() => sortByProp('label')(roles.map(makeOption)), [roles])

  const selectedOption = value ? options.find((option) => option.value === value) : null

  return (
    <Select
      className="refinery-select"
      classNamePrefix="Select"
      isDisabled={isReadonly}
      onChange={onChange}
      options={options}
      placeholder={I18n.t('tensions.react.role_selector_placeholder')}
      value={selectedOption}
    />
  )
}

export default SelectRole
